import * as React from 'react'
import { Bot } from 'lucide-react'
import { cn } from '@/lib/utils'

// ============================================================================
// AgentToolIcon
//
// Only Claude Code actually installs today (see AgentInstallManager.tsx /
// src/lib/agentInstall.ts — the install prompt and hook script are
// Claude-Code-specific), so agent.client_name is always "Claude Code" in
// practice right now. `agents.client_name` is a free-text column, not an
// enum, and the design team supplied brand icons for Claude, Cursor, and
// Codex together — so this maps by name today and is ready for the other
// two the moment an install flow for them exists, without needing a schema
// or UI change at that point. Anything that doesn't match falls back to a
// generic bot icon rather than guessing.
//
// ChatGPT (added to src/lib/agentTools.ts's AGENT_TOOLS 2026-10-08, as a
// disabled "coming soon" tile) has no brand asset here yet -- ask the
// design team for a chatgpt.svg the same way Claude/Cursor/Codex's were
// supplied, then add it below. Deliberately NOT pointed at a placeholder
// path in the meantime: a missing file renders as a broken <img>, which is
// worse than the generic bot icon it falls back to today.
// ============================================================================

const TOOL_ICON_BY_MATCH: { match: RegExp; src: string; alt: string }[] = [
  { match: /claude/i, src: '/agent-tools/claude.svg', alt: 'Claude' },
  { match: /cursor/i, src: '/agent-tools/cursor.svg', alt: 'Cursor' },
  { match: /codex/i, src: '/agent-tools/codex.svg', alt: 'Codex' },
]

interface AgentToolIconProps {
  clientName?: string | null
  className?: string
}

export function AgentToolIcon({ clientName, className }: AgentToolIconProps) {
  const tool = clientName
    ? TOOL_ICON_BY_MATCH.find(({ match }) => match.test(clientName))
    : undefined

  if (!tool) {
    return <Bot className={cn('text-muted-foreground', className)} aria-hidden="true" />
  }

  return (
    <img
      src={tool.src}
      alt={tool.alt}
      className={cn('rounded-[6px]', className)}
    />
  )
}
